import { EntityBase } from './entityBase';

import type {
  CreateEntityInput,
  DeleteEntityInput,
  EntityName,
  UpdateEntityInput,
} from '@/infra/appsync';

/**
 * 削除タイプ
 */
export enum DeleteType {
  /** 時間 */
  Dt = '-Dt',
  /** 親子ID */
  ParentChildId = 'ParentChildId',
  /** 親ID */
  ParentId = 'ParentId',
  /** 施設ID */
  NurseryId = 'NurseryId',
  /** 掲示板カテゴリID */
  NoticeCategoryId = 'NoticeCategoryId',
  /** プライベートブログカテゴリID */
  PrivateBlogCategoryId = 'PrivateBlogCategoryId'
}

/**
 * Entityの抽象クラス.
 */
export abstract class EntityRepositoryBase<P, J, PP, JP> extends EntityBase<P, J, PP, JP> {
  /** Entity名 */
  abstract get entityName(): EntityName;

  /** 削除タイプ */
  abstract get deleteType(): DeleteType;

  /** 削除キー */
  abstract get deleteKey(): string;

  /**
   * 将来の日付を取得
   * @param date 日付
   * @param numDate 何日将来か
   * @returns 将来の日付
   */
  protected getFutureDate(date: Date, numDate: number) {
    const dt = new Date(date.getTime());
    dt.setDate(dt.getDate() + numDate);
    return dt;
  }

  /**
   * 将来の日付を取得
   * @param date 日付
   * @param numMonth 何か月後か
   * @returns 将来の日付
   */
  protected getFutureMonth(date: Date, numMonth: number) {
    const dt = new Date(date.getTime());
    dt.setMonth(dt.getMonth() + numMonth);
    return dt;
  }

  /**
   * 将来の日付を取得
   * @param date 日付
   * @param numYear 何年後か
   * @returns 将来の日付
   */
  protected getFutureYear(date: Date, numYear: number) {
    const dt = new Date(date.getTime());
    dt.setFullYear(dt.getFullYear() + numYear);
    return dt;
  }

  /**
   * キー
   */
  get key(): string {
    return `${this.entityName}#${this.entityKey}`;
  }

  /**
   * キーが同じか
   * @param key キー
   * @returns 同じか
   */
  isEqualKey(key: string): boolean {
    return key === this.key;
  }

  /**
   * AppsyncのCreateEntity用入力生成.
   * @returns CreateEntityInput
   */
  toCreateEntityInput(): CreateEntityInput {
    return {
      entityName: this.entityName,
      entityKey: this.entityKey,
      deleteDt: `${this.deleteType}#${this.deleteKey}`,
      subKey: this.subKey ? this.subKey : null,
      subKey2: this.subKey2 ? this.subKey2 : null,
      gsiKey: this.gsiKey,
      data: this.data,
      pub: this.pub,
    };
  }

  /**
   * AppsyncのUpdateEntity用入力生成.
   * @returns CreateEntityInput
   */
  toUpdateEntityInput(): UpdateEntityInput {
    return {
      entityName: this.entityName,
      entityKey: this.entityKey,
      data: this.data,
      pub: this.pub,
    };
  }

  /**
   * AppsyncのDeleteEntity用入力生成.
   * @returns CreateEntityInput
   */
  toDeleteEntityInput(): DeleteEntityInput {
    return {
      entityName: this.entityName,
      entityKey: this.entityKey,
      deleteDt: `${this.deleteType}#${this.deleteKey}`,
      subKey: this.subKey ? `${this.entityName}#${this.subKey}` : null,
      subKey2: this.subKey2 ? `${this.entityName}#${this.subKey2}` : null,
      gsiKey: this.gsiKey,
      data: this.data,
      pub: this.pub,
    };
  }
}
